import { useState } from "react";
import { type AxiosError } from "axios";
import { type UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getAxiosErrorData } from "@utils/common";
import { type IApiErrorResponse } from "@/types";

export interface IValidationError {
  fieldName: string;
  translationKey: string;
  text: string;
  additionalData?: Record<string, string | number>;
}

export const useApiErrorUtils = (
  setFormError?: UseFormSetError<Record<string, never>>,
): {
  setApiErrors: (err: AxiosError, toastUnknownError?: boolean) => undefined;
  getApiErrors: (
    err: AxiosError,
    toastUnknownError?: boolean,
    includeWithoutPlaceholder?: boolean,
  ) => IValidationError[];
  toastApiError: (err: AxiosError) => undefined;
  validationErrors: IValidationError[];
  clearErrors: () => void;
} => {
  const { t, i18n } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<IValidationError[]>(
    [],
  );

  const parseApiErrors = (
    apiError: IApiErrorResponse,
    toastUnknownError = true,
    includeWithoutPlaceholder = false,
  ): IValidationError[] => {
    const validValidationErrors = (apiError?.errors ?? []).filter(
      (err) =>
        err.errorCode === "ValidationError" &&
        (includeWithoutPlaceholder || !!err.placeholder) &&
        !!err.messageCode,
    );

    if (!validValidationErrors.length) {
      // If there are no validation errors, we can show a toast with other type of errors
      if (toastUnknownError) {
        if (apiError?.errors?.[0]?.messageCode ?? apiError?.messageCode) {
          const translationKey = `validationErrors.${(apiError.errors ?? [])[0]?.messageCode ?? apiError?.messageCode}`;

          if (i18n.exists(translationKey)) {
            toast.error(t(translationKey));
            return [];
          }
        }

        toast.error(t("common.unknownErrorOccurred"));
        return [];
      }
      return [];
    }

    return validValidationErrors.map((err) => ({
      fieldName: err.placeholder ?? "",
      translationKey: err.messageCode ?? "",
      text: err.messageCode ? t(`validationErrors.${err.messageCode}`) : "",
      ...(err.additionalData ? { additionalData: err.additionalData } : {}),
    }));
  };

  const parseApiErrorMessageCodes = (
    apiError: IApiErrorResponse,
  ): string[] | undefined => {
    return apiError.errors?.reduce<string[]>(
      (validErrors, error) =>
        (error.errorCode === "ValidationError" ||
          error.errorCode === "PermissionError") &&
        !!error.messageCode
          ? [...validErrors, error.messageCode]
          : validErrors,
      [],
    );
  };

  const toastApiError = (errorResponse: AxiosError) => {
    const errorMessageCodes = parseApiErrorMessageCodes(
      getAxiosErrorData(errorResponse),
    );

    if (errorMessageCodes?.length) {
      toast.error(t(`validationErrors.${errorMessageCodes[0]}`));
    } else {
      toast.error(t("common.unknownErrorOccurred"));
    }

    return undefined;
  };

  const getApiErrors = (
    errorResponse: AxiosError,
    toastUnknownError = true,
    includeWithoutPlaceholder = false,
  ): IValidationError[] => {
    return parseApiErrors(
      getAxiosErrorData(errorResponse),
      toastUnknownError,
      includeWithoutPlaceholder,
    );
  };

  const setApiErrors = (
    errorResponse: AxiosError,
    toastUnknownError = true,
  ): undefined => {
    const errors = getApiErrors(errorResponse, toastUnknownError);

    setValidationErrors(errors);

    if (setFormError) {
      errors.forEach((error) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        setFormError(error.fieldName, {
          type: "custom",
          message: error.text,
        });
      });
    }

    return undefined;
  };

  const clearErrors = () => {
    setValidationErrors([]);
  };

  return {
    setApiErrors,
    getApiErrors,
    toastApiError,
    validationErrors,
    clearErrors,
  };
};
